import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { parseCanvasJson, type Agent, type AgentNetwork, type CanvasDocument, type Conversation, type Run } from '@orqforge/shared';
import { apiClient, type RunDetailSnapshot, type RunOutputFile, type UploadedFileInfo } from '../lib/api-client';
import { authHeaders } from '../lib/http';
import { sseUrl } from '../lib/sse';
import { useApp } from '../contexts/AppContext';
import { collaborateGridClass, pickLatestCanvasFile, readCanvasText } from '../components/collaborate/canvas-files';
import { foldDictateResults, type DictateResultsLike } from '../components/collaborate/dictate';
import { buildCollaborateEntryModel, findSelectedAgent } from '../components/collaborate/routing';
import { applyAgentStreamEnvelope, appendMarker, buildThreadFromHistory, mergeCarryOverStreaming, upsertClarification, upsertGate, type AgentStreamEnvelope, type ThreadItem } from '../components/collaborate/thread';
import { CanvasPane } from '../components/collaborate/CanvasPane';
import { ChatThread, type ClarificationAnswer } from '../components/collaborate/ChatThread';
import type { GateDecision } from '../components/collaborate/HumanGateCard';
import { RunProgress } from '../components/runs/RunProgress';
import { SessionList } from '../components/collaborate/SessionList';
import { Button } from '../components/ui/Button';

const ACTIVE = new Set(['queued', 'running', 'waiting_approval', 'waiting_review', 'waiting_input', 'escalated']);
const NEEDS_YOU = new Set(['waiting_approval', 'waiting_review', 'waiting_input', 'escalated']);

interface SpeechLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { results: DictateResultsLike; resultIndex?: number }) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

function speechCtor(): (new () => SpeechLike) | null {
  const win = window as Window & { SpeechRecognition?: new () => SpeechLike; webkitSpeechRecognition?: new () => SpeechLike };
  return win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null;
}

function isTerminal(status: string): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled' || status === 'changes_requested';
}

export function CollaboratePage() {
  const { tenantId, workspaceId } = useApp();
  const [params, setParams] = useSearchParams();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentId, setAgentId] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(params.get('conversationId'));
  const [allRuns, setAllRuns] = useState<Run[]>([]);
  const [items, setItems] = useState<ThreadItem[]>([]);
  const [network, setNetwork] = useState<AgentNetwork | null>(null);
  const [stages, setStages] = useState<RunDetailSnapshot['stages']>([]);
  const [sending, setSending] = useState(false);
  const [running, setRunning] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<UploadedFileInfo[]>([]);
  const [outputFiles, setOutputFiles] = useState<RunOutputFile[]>([]);
  const [canvasDoc, setCanvasDoc] = useState<CanvasDocument | null>(null);
  const [canvasBusy, setCanvasBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [dictating, setDictating] = useState(false);
  const activeRunId = useRef<string | undefined>(undefined);
  const detailSource = useRef<EventSource | undefined>(undefined);
  const streamSource = useRef<EventSource | undefined>(undefined);
  const loadEpoch = useRef(0);
  const recognition = useRef<SpeechLike | null>(null);
  const wantDictate = useRef(false);
  const dictateBase = useRef('');
  const fileRef = useRef<HTMLInputElement>(null);
  const speechSupported = typeof window !== 'undefined' && Boolean(speechCtor());

  const entry = useMemo(() => buildCollaborateEntryModel(agents), [agents]);
  const selected = findSelectedAgent(agents, agentId);
  const showEntry = !selectedConversationId && items.length === 0;
  const sessionTitle = conversations.find((row) => row.id === selectedConversationId)?.title ?? '';
  const badges = useMemo(() => {
    const active = new Set<string>();
    const needsYou = new Set<string>();
    for (const run of allRuns) {
      if (!run.conversationId) continue;
      if (ACTIVE.has(run.status)) active.add(run.conversationId);
      if (NEEDS_YOU.has(run.status)) needsYou.add(run.conversationId);
    }
    return { active, needsYou };
  }, [allRuns]);

  const busyLabel = sending
    ? (running ? 'queuing...' : 'starting...')
    : !running
      ? ''
      : items.some((item) => item.type === 'agent' && item.streaming)
        ? ''
        : items.some((item) => item.type === 'user' && item.queued)
          ? 'message queued, applying next turn...'
          : `${selected?.name ?? 'Agent'} working...`;

  function syncUrl(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set('conversationId', id);
    else next.delete('conversationId');
    setParams(next, { replace: true });
  }

  function refreshConversations() {
    void apiClient.listConversations().then(setConversations).catch(() => setConversations([]));
  }

  function resetWorkspace() {
    loadEpoch.current += 1;
    teardownRunStreams();
    activeRunId.current = undefined;
    setRunning(false);
    setStopping(false);
    setItems([]);
    setNetwork(null);
    setStages([]);
    setAttachments([]);
    setOutputFiles([]);
    setSending(false);
  }

  function applyDetail(snapshot: RunDetailSnapshot) {
    setNetwork(snapshot.network);
    setStages(snapshot.stages);
    setOutputFiles(snapshot.files);
    setItems((current) => {
      let next = current;
      for (const clarification of snapshot.clarifications ?? []) {
        next = upsertClarification(next, {
          type: 'clarification',
          id: `clarification:${clarification.id}`,
          runId: clarification.runId,
          question: clarification.question,
          answer: clarification.answer ?? undefined,
          status: clarification.status,
          at: String(clarification.askedAt ?? new Date().toISOString()),
        });
      }
      for (const approval of snapshot.approvals ?? []) {
        next = upsertGate(next, {
          type: 'gate',
          id: `approval:${approval.id}`,
          gateKind: 'approval',
          runId: approval.runId,
          status: approval.status,
          data: approval as unknown as Record<string, unknown>,
          at: String(approval.createdAt ?? new Date().toISOString()),
        });
      }
      for (const gate of snapshot.reviewGates ?? []) {
        if (gate.reviewerKind !== 'human') continue;
        next = upsertGate(next, {
          type: 'gate',
          id: `review:${gate.id}`,
          gateKind: 'review',
          runId: gate.runId,
          status: gate.status === 'pending' ? 'pending' : gate.status === 'changes_requested' ? 'rejected' : 'approved',
          data: gate as unknown as Record<string, unknown>,
          at: String(gate.createdAt ?? new Date().toISOString()),
        });
      }
      for (const stage of snapshot.stages) {
        if (stage.status === 'pending' || stage.status === 'active') continue;
        next = appendMarker(next, {
          type: 'marker',
          id: `stage:${stage.id}`,
          label: `${stage.stage} ${stage.status}`,
          tone: stage.status === 'done' ? 'success' : stage.status === 'cancelled' ? 'error' : 'info',
          at: String(stage.endedAt ?? stage.startedAt ?? new Date().toISOString()),
        });
      }
      return next;
    });
    if (isTerminal(snapshot.run.status) && activeRunId.current === snapshot.run.id) {
      activeRunId.current = undefined;
      setRunning(false);
      setStopping(false);
      refreshConversations();
    }
  }

  function teardownRunStreams() {
    detailSource.current?.close();
    streamSource.current?.close();
    detailSource.current = undefined;
    streamSource.current = undefined;
  }

  async function attachToRun(runId: string) {
    teardownRunStreams();
    activeRunId.current = runId;
    setRunning(true);
    void apiClient.getRunDetailSnapshot(runId).then((snapshot) => {
      if (activeRunId.current === runId) applyDetail(snapshot);
    }).catch(() => undefined);
    if (typeof EventSource === 'undefined') return;
    const detail = new EventSource(await sseUrl(`/api/runs/${runId}/events`));
    detailSource.current = detail;
    detail.addEventListener('run-detail', (event) => {
      try {
        applyDetail(JSON.parse((event as MessageEvent).data) as RunDetailSnapshot);
      } catch {
        // Ignore malformed stream payloads.
      }
    });
    const openStream = async () => {
      streamSource.current?.close();
      const source = new EventSource(await sseUrl(`/api/runs/${runId}/stream/events`));
      streamSource.current = source;
      source.addEventListener('agent-stream', (event) => {
        try {
          const envelope = JSON.parse((event as MessageEvent).data) as AgentStreamEnvelope;
          setItems((current) => {
            const result = applyAgentStreamEnvelope(current, envelope);
            return result.items;
          });
        } catch {
          // Ignore malformed stream payloads.
        }
      });
      source.onerror = () => {
        source.close();
        if (activeRunId.current === runId) setTimeout(() => void openStream(), 3000);
      };
    };
    await openStream();
  }

  async function restoreSession(conversationId: string, carryOver = false) {
    const epoch = loadEpoch.current;
    const previous = carryOver ? items : [];
    try {
      const [messages, runs] = await Promise.all([
        apiClient.getMessages(conversationId),
        apiClient.listRuns({ conversationId }),
      ]);
      if (epoch !== loadEpoch.current) return;
      const conversation = conversations.find((row) => row.id === conversationId);
      if (conversation) setAgentId(conversation.agentId);
      else if (runs[0]) setAgentId(runs[0].agentId);
      const steps = await Promise.all(runs.map((run) => apiClient.getRunSteps(run.id)));
      const clarifications = await Promise.all(runs.map((run) => apiClient.getRunClarifications(run.id)));
      if (epoch !== loadEpoch.current) return;
      const stepsByRun: Record<string, ReturnType<typeof buildThreadFromHistory> extends never ? never : never> = {};
      const mapped: Record<string, Array<{ id: string; runId: string; agentId: string; agentName?: string; index: number; kind: string; content: Record<string, unknown>; createdAt: string }>> = {};
      runs.forEach((run, index) => {
        mapped[run.id] = steps[index].steps.map((step) => ({
          id: step.id,
          runId: step.runId,
          agentId: step.agentId,
          agentName: step.agentName,
          index: step.index,
          kind: step.kind,
          content: step.content ?? {},
          createdAt: String(step.createdAt),
        }));
      });
      void stepsByRun;
      const rebuilt = buildThreadFromHistory({
        messages: messages.map((row) => ({ id: String(row.id), role: row.role, content: row.content, createdAt: String(row.createdAt ?? new Date().toISOString()) })),
        runs: runs.map((run) => ({ id: run.id, agentId: run.agentId, status: run.status, output: run.output ?? null, error: run.error ?? null, createdAt: String(run.createdAt), updatedAt: String(run.updatedAt) })),
        stepsByRun: mapped,
        clarifications: clarifications.flat().map((row) => ({ id: row.id, runId: row.runId, question: row.question, answer: row.answer, status: row.status, askedAt: String(row.askedAt ?? '') })),
        agentName: selected?.name,
      });
      setItems(carryOver ? mergeCarryOverStreaming(rebuilt, previous) : rebuilt);
      const active = [...runs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).find((run) => ACTIVE.has(run.status));
      if (active) void attachToRun(active.id);
      else if (runs[0]) {
        const snapshot = await apiClient.getRunDetailSnapshot(runs[0].id);
        if (epoch === loadEpoch.current) {
          setNetwork(snapshot.network);
          setStages(snapshot.stages);
          setOutputFiles(snapshot.files);
        }
      }
    } catch {
      if (epoch !== loadEpoch.current) return;
      setItems([{ type: 'marker', id: 'restore-error', label: 'Failed to load this session. Try again.', tone: 'error', at: new Date().toISOString() }]);
    }
  }

  useEffect(() => {
    void apiClient.listAgents().then((page) => {
      setAgents(page.items);
      const model = buildCollaborateEntryModel(page.items);
      setAgentId((current) => current || model.defaultAgent?.id || '');
    }).catch(() => setAgents([]));
    refreshConversations();
    void apiClient.listRuns().then(setAllRuns).catch(() => setAllRuns([]));
    const initial = params.get('conversationId');
    if (initial) {
      setSelectedConversationId(initial);
      void restoreSession(initial);
    }
    let source: EventSource | undefined;
    void (async () => {
      if (typeof EventSource === 'undefined') return;
      source = new EventSource(await sseUrl('/api/runs/events', { workspaceId }));
      source.addEventListener('run-deltas', (event) => {
        try {
          const payload = JSON.parse((event as MessageEvent).data) as { runs: Run[] };
          setAllRuns((current) => {
            const byId = new Map(current.map((run) => [run.id, run]));
            for (const run of payload.runs) byId.set(run.id, run);
            return [...byId.values()];
          });
        } catch {
          // Ignore malformed stream payloads.
        }
      });
    })();
    return () => source?.close();
  }, [workspaceId]);

  useEffect(() => {
    const file = pickLatestCanvasFile(outputFiles);
    if (!file?.url && !file?.contentBase64) {
      setCanvasDoc(null);
      return;
    }
    const inline = readCanvasText(file);
    if (inline !== null) {
      setCanvasDoc(parseCanvasJson(inline));
      return;
    }
    if (!file.url) {
      setCanvasDoc(null);
      return;
    }
    void authHeaders().then((headers) => fetch(file.url!, { headers }).then((res) => res.text()).then((raw) => setCanvasDoc(parseCanvasJson(raw))).catch(() => setCanvasDoc(null)));
  }, [outputFiles]);

  function stopDictate() {
    wantDictate.current = false;
    const current = recognition.current;
    recognition.current = null;
    setDictating(false);
    if (!current) return;
    try {
      current.onresult = null;
      current.onerror = null;
      current.onend = null;
      current.stop();
    } catch {
      // Already stopped.
    }
  }

  function beginRecognition() {
    const Ctor = speechCtor();
    if (!Ctor) {
      wantDictate.current = false;
      setDictating(false);
      return;
    }
    dictateBase.current = message.trimEnd();
    const next = new Ctor();
    next.continuous = true;
    next.interimResults = true;
    next.lang = navigator.language || 'en-US';
    next.onresult = (event) => {
      const folded = foldDictateResults(dictateBase.current, event.results, event.resultIndex ?? 0);
      dictateBase.current = folded.base;
      setMessage(folded.text);
    };
    next.onerror = (event) => {
      const error = (event as { error?: string }).error ?? '';
      if (error === 'no-speech' || error === 'aborted') return;
      wantDictate.current = false;
      setDictating(false);
      recognition.current = null;
    };
    next.onend = () => {
      if (recognition.current !== next) return;
      recognition.current = null;
      if (!wantDictate.current) {
        setDictating(false);
        return;
      }
      setTimeout(() => {
        if (!wantDictate.current || recognition.current) return;
        beginRecognition();
      }, 0);
    };
    recognition.current = next;
    setDictating(true);
    try {
      next.start();
    } catch {
      wantDictate.current = false;
      setDictating(false);
      recognition.current = null;
    }
  }

  async function send() {
    if (!message.trim() && !attachments.length) return;
    if (!agentId || sending || stopping) return;
    setSending(true);
    stopDictate();
    const wasRunning = running;
    if (!wasRunning) {
      setRunning(true);
      setOutputFiles([]);
    }
    const text = message.trim();
    const uploadIds = attachments.map((file) => file.id);
    setMessage('');
    setAttachments([]);
    const now = new Date().toISOString();
    const localId = `local:${now}`;
    setItems((current) => [...current, { type: 'user', id: localId, content: text, at: now, queued: wasRunning || undefined }]);
    try {
      const res = await apiClient.sendChatMessage(agentId, text, selectedConversationId ?? undefined, uploadIds.length ? uploadIds : undefined);
      setSending(false);
      if (!selectedConversationId) {
        setSelectedConversationId(res.conversationId);
        syncUrl(res.conversationId);
      }
      setItems((current) => current.map((item) => item.id === localId && item.type === 'user' ? { ...item, id: `msg:${res.messageId}`, queued: res.status === 'queued' || undefined } : item));
      refreshConversations();
      if (res.runId && (res.status === 'started' || !wasRunning || (res.status === 'queued' && !network))) void attachToRun(res.runId);
    } catch (err) {
      setSending(false);
      if (!wasRunning) setRunning(false);
      const detail = err instanceof Error ? err.message : 'unknown error';
      setItems((current) => [...current.filter((item) => item.id !== localId), { type: 'marker', id: `error:${Date.now()}`, label: `Send failed: ${detail}`, tone: 'error', at: new Date().toISOString() }]);
    }
  }

  if (!tenantId) {
    return (
      <div className="py-16 text-center">
        <h2 className="mb-2 text-xl font-semibold">No tenant selected</h2>
        <p className="mb-4 text-sm text-gray-500">Select a tenant from the header dropdown or go to the Tenants page to create one first.</p>
        <Link to="/tenants"><Button size="sm">Go to Tenants</Button></Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Collaborate</h1>
          <p className="text-sm text-gray-500">Work with your agents in real time. Sessions persist and stream live agent activity.</p>
        </div>
      </div>
      <div className={`mt-5 grid gap-4 ${collaborateGridClass(Boolean(canvasDoc))}`}>
        <aside className="rounded-xl border border-gray-200 bg-gray-50 p-2.5 dark:border-gray-700 dark:bg-gray-800/40">
          <SessionList
            conversations={conversations}
            selectedId={selectedConversationId}
            badges={badges}
            agentNames={agents.map((agent) => ({ id: agent.id, name: agent.name }))}
            onSelect={(id) => {
              if (selectedConversationId === id) return;
              setSelectedConversationId(id);
              syncUrl(id);
              resetWorkspace();
              void restoreSession(id);
            }}
            onNew={() => {
              setSelectedConversationId(null);
              syncUrl(null);
              resetWorkspace();
              if (entry.defaultAgent) setAgentId(entry.defaultAgent.id);
            }}
          />
        </aside>
        <div className="flex min-w-0 flex-col gap-4">
          {showEntry && (
            <div className="grid gap-4 lg:grid-cols-2">
              {entry.defaultAgent ? (
                <button type="button" className={`rounded-lg border p-4 text-left ${agentId === entry.defaultAgent.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'}`} onClick={() => setAgentId(entry.defaultAgent!.id)}>
                  <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Start here</div>
                  <h2 className="text-xl font-semibold">Submit a request</h2>
                  <p className="mt-1 text-sm text-gray-500">Use {entry.defaultAgent.name} when you have a file, prompt, or open-ended request.</p>
                </button>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 p-4">
                  <h2 className="text-lg font-semibold">{entry.emptyStateTitle}</h2>
                </div>
              )}
              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                <h2 className="text-base font-semibold">Talk to a specific role</h2>
                <div className="mt-2 flex flex-wrap gap-2">
                  {entry.selectableAgents.map((agent) => (
                    <button key={agent.id} type="button" className={`rounded border px-3 py-2 text-sm ${agentId === agent.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`} onClick={() => setAgentId(agent.id)}>{agent.name}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {selected && (
            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">{selectedConversationId ? 'Session' : 'New session'}</div>
                  <h2 className="text-lg font-semibold">{sessionTitle || selected.name}</h2>
                  <p className="text-sm text-gray-500">{selected.name} · {selected.agentRole}</p>
                </div>
                {running && <Button size="sm" variant="outline" disabled={stopping} onClick={() => {
                  const runId = activeRunId.current;
                  if (!runId || stopping) return;
                  setStopping(true);
                  void apiClient.cancelRun(runId).catch(() => setStopping(false));
                }}>{stopping ? 'Stopping...' : 'Stop'}</Button>}
              </div>
              {running && <div className="mt-3"><RunProgress stages={stages} nodes={network?.nodes ?? []} /></div>}
              <div className="mt-3">
                <ChatThread
                  items={items}
                  networkNodes={network?.nodes ?? []}
                  fallbackAgentName={selected.name}
                  busyLabel={busyLabel}
                  onAnswer={(event: ClarificationAnswer) => {
                    const id = event.clarification.id.replace(/^clarification:/, '');
                    void apiClient.answerClarification(event.clarification.runId, id, event.answer).then((updated) => {
                      setItems((current) => upsertClarification(current, { ...event.clarification, answer: updated.answer ?? event.answer, status: 'answered' }));
                    });
                  }}
                  onDecide={(event: GateDecision) => {
                    const rawId = event.gate.id.replace(/^(approval|review):/, '');
                    const request = event.gate.gateKind === 'approval'
                      ? apiClient.decideApproval(rawId, event.approve, event.reason)
                      : apiClient.decideReview(rawId, event.approve, event.reason);
                    void request.then(() => {
                      setItems((current) => upsertGate(current, { ...event.gate, status: event.approve ? 'approved' : 'rejected', data: { ...event.gate.data, reason: event.reason } }));
                    });
                  }}
                />
              </div>
              {outputFiles.length > 0 && (
                <div className="mt-3 rounded-xl border border-green-200 bg-green-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-green-700">Pipeline outputs ready</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {outputFiles.map((file) => (
                      <div key={file.uploadId ?? file.filename} className="rounded border border-gray-200 bg-white px-3 py-2 text-xs">
                        <div className="font-medium">{file.filename}</div>
                        {file.url && <a className="text-blue-600" href={file.url}>Download</a>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-gray-200 p-2">
                <input ref={fileRef} type="file" multiple className="hidden" onChange={(event) => {
                  const files = event.target.files ? Array.from(event.target.files) : [];
                  event.target.value = '';
                  if (!files.length) return;
                  setUploading(true);
                  void Promise.all(files.map(async (file) => {
                    const record = await apiClient.uploadFile(file);
                    const url = await apiClient.getUploadUrl(record.id);
                    return { id: record.id, filename: record.filename, mimeType: record.mimeType, sizeBytes: record.sizeBytes, url: url.url };
                  })).then((uploaded) => setAttachments((current) => [...current, ...uploaded])).finally(() => setUploading(false));
                }} />
                <Button size="sm" variant="ghost" disabled={uploading} data-testid="collaborate-attach" title="Attach chat files. Invoice PDFs start in Mailbox." aria-label="Attach chat files. Invoice PDFs start in Mailbox." onClick={() => fileRef.current?.click()}>Attach</Button>
                <input className="flex-1 bg-transparent px-2 text-sm outline-none" placeholder="Describe the request, steer the run, or ask a question..." value={message} disabled={sending || stopping} onChange={(event) => setMessage(event.target.value)} onKeyUp={(event) => { if (event.key === 'Enter') void send(); }} />
                {speechSupported && (
                  <button type="button" className={`rounded px-2 py-1 text-sm ${dictating ? 'bg-red-100 text-red-600' : ''}`} data-testid="dictate" aria-pressed={dictating} disabled={sending || stopping} title={dictating ? 'Stop dictation' : 'Dictate with microphone'} onClick={() => {
                    if (wantDictate.current || dictating) stopDictate();
                    else {
                      wantDictate.current = true;
                      beginRecognition();
                    }
                  }}>Mic</button>
                )}
                <Button size="sm" data-testid="collaborate-send" disabled={sending || stopping || (!message.trim() && !attachments.length)} onClick={() => void send()}>Send</Button>
              </div>
            </div>
          )}
        </div>
        {canvasDoc && (
          <CanvasPane document={canvasDoc} executing={canvasBusy} onAction={(event) => {
            setCanvasBusy(true);
            void apiClient.executeTool(event.toolKey, event.arguments).then((res) => {
              if (res.canvas) setCanvasDoc(res.canvas);
            }).finally(() => setCanvasBusy(false));
          }} />
        )}
      </div>
    </div>
  );
}
